import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  DoorOpen,
  Flame,
  Gamepad2,
  Grid2X2,
  Trash2,
  UsersRound,
  type LucideIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { roomName } from "@/engine/state/roomIdentity";
import type { SavedRoom } from "@/engine/state/savedRooms";
import { cn } from "@/engine/support/classNames";

export interface HomeRoomsProps {
  /** The rooms this visitor can go back to, newest first. */
  rooms: readonly SavedRoom[];
  /** How many are standing in each, for the rooms that answered. */
  counts?: Record<string, number> | undefined;
  onOpen: (code: string) => void;
  /** Gives up a hosted room for good; resolves false where it was refused. */
  onForget?: ((code: string) => Promise<boolean>) | undefined;
}

/**
 * A signed-in visitor's rooms, as two halves of one list: the rooms they host
 * and the rooms they were a guest in. Hosting is what keeps a room alive and
 * what lets somebody delete it, so the two are not one list sorted together.
 */
export function HomeRooms({ rooms, counts, onOpen, onForget }: HomeRoomsProps) {
  const { t } = useTranslation();
  return (
    <section
      data-testid="home-rooms"
      aria-label={t("entry.yourRooms")}
      className="space-y-6"
    >
      <RoomGroup
        testId="home-rooms-hosted"
        title={t("entry.roomsHosted")}
        hint={t("entry.roomsHostedHint")}
        rooms={rooms.filter((room) => room.owned)}
        counts={counts}
        onOpen={onOpen}
        onForget={onForget}
      />
      <RoomGroup
        testId="home-rooms-visited"
        title={t("entry.roomsVisited")}
        hint={t("entry.roomsVisitedHint")}
        rooms={rooms.filter((room) => !room.owned)}
        counts={counts}
        onOpen={onOpen}
      />
    </section>
  );
}

/** The kind's own mark, as the room-type cards draw it. */
const MODE_ICON: Record<string, LucideIcon> = {
  campfire: Flame,
  bubbles: UsersRound,
  plain: Grid2X2,
  teams: Gamepad2,
};

/** How many rooms a group lists before it asks. */
const ROOMS_SHOWN = 6;

/**
 * The line under a room's name, as the demo writes it: what kind of room it
 * is, and how many are in it where the room answered — "campfire · 4".
 */
function roomMeta(
  t: TFunction,
  mode: string | undefined,
  count: number | undefined,
): string {
  const kind = mode ? t(`roomMode.${mode}`).toLocaleLowerCase() : null;
  // Only an answer is drawn. A room that has not replied says nothing rather
  // than nought: "empty" on a room that is merely out of reach is the screen
  // making something up.
  const people =
    count === undefined
      ? null
      : count > 0
        ? t("entry.roomHere", { count })
        : t("entry.roomEmpty");
  return [kind, people].filter(Boolean).join(" · ");
}

function RoomGroup({
  testId,
  title,
  hint,
  rooms,
  counts,
  onOpen,
  onForget,
}: {
  testId: string;
  title: string;
  hint: string;
  rooms: readonly SavedRoom[];
  counts?: Record<string, number> | undefined;
  onOpen: (code: string) => void;
  onForget?: ((code: string) => Promise<boolean>) | undefined;
}) {
  const { t } = useTranslation();
  // A host with a season of rooms behind them gets the newest few and the rest
  // on asking, rather than a column several screens tall (#762).
  const [all, setAll] = useState(false);
  const shown = all ? rooms : rooms.slice(0, ROOMS_SHOWN);
  return (
    <div data-testid={testId} className="min-w-0 space-y-2.5 sm:space-y-3">
      <div className="space-y-1">
        <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      {rooms.length > 0 ? (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((room) => (
            <li key={room.code}>
              <HomeRoomRow
                room={room}
                count={counts?.[room.code]}
                onOpen={onOpen}
                onForget={onForget}
              />
            </li>
          ))}
          {shown.length < rooms.length && (
            <li className="sm:col-span-2 lg:col-span-3">
              <Button
                variant="ghost"
                size="sm"
                data-testid={`${testId}-all`}
                className="w-full text-muted-foreground"
                onClick={() => setAll(true)}
              >
                {t("entry.roomsShowAll", { count: rooms.length })}
              </Button>
            </li>
          )}
        </ul>
      ) : (
        // Said rather than left blank, so the two halves read as one list.
        <p className="rounded-2xl border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
          {t("entry.roomsNone")}
        </p>
      )}
    </div>
  );
}

/**
 * One room, and the way to give it up. Deleting is offered only for a room the
 * visitor hosts, and only behind a second press: the code stops opening for
 * everybody who has it. The ask replaces the row rather than floating over it,
 * so nothing moves under the pointer.
 */
function HomeRoomRow({
  room,
  count,
  onOpen,
  onForget,
}: {
  room: SavedRoom;
  count?: number | undefined;
  onOpen: (code: string) => void;
  onForget?: ((code: string) => Promise<boolean>) | undefined;
}) {
  const { t } = useTranslation();
  const [asking, setAsking] = useState(false);
  // A refusal is said where it happened. The row stays, because the room does.
  const [failed, setFailed] = useState(false);
  const row =
    "flex min-h-20 items-center gap-3 rounded-2xl border border-border bg-background p-4";
  const Icon = MODE_ICON[room.mode ?? ""] ?? DoorOpen;
  const title = room.name ?? roomName(room.code);
  const meta = roomMeta(t, room.mode, count);

  if (asking) {
    return (
      <div data-testid="home-room-ask" className={cn(row, "bg-raise")}>
        <span className="min-w-0 flex-1 truncate text-sm">
          {t("entry.deleteRoomConfirm")}
        </span>
        <Button
          size="sm"
          variant="ghost"
          data-testid="home-room-delete-confirm"
          className="text-destructive"
          onClick={() => {
            setAsking(false);
            void onForget?.(room.code).then((gone) => setFailed(!gone));
          }}
        >
          {t("entry.deleteRoom")}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          data-testid="home-room-delete-cancel"
          onClick={() => setAsking(false)}
        >
          {t("entry.deleteRoomCancel")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {failed && (
        <p
          data-testid="home-room-delete-failed"
          className="px-1 text-xs text-destructive"
        >
          {t("entry.deleteRoomFailed")}
        </p>
      )}
      <div
        data-testid="home-room"
        data-code={room.code}
        className={cn(
          row,
          "relative transition-colors duration-(--motion-quick) focus-within:border-primary/50 hover:border-primary/50 hover:bg-raise/40",
        )}
      >
        <span
          aria-hidden="true"
          className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary/12 text-primary"
        >
          <Icon className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          {/* The whole card opens the room: its name is the button, stretched
              over the card, so the one thing on top of it is the delete. */}
          <button
            type="button"
            data-testid="home-room-open"
            aria-label={`${t("entry.roomOpen")}: ${title}`}
            onClick={() => onOpen(room.code)}
            className="block w-full truncate text-left font-medium outline-none after:absolute after:inset-0 after:rounded-2xl after:content-['']"
          >
            {title}
          </button>
          {meta && (
            <p
              data-testid="home-room-count"
              className={cn(
                "truncate text-xs tabular-nums",
                count ? "text-primary" : "text-muted-foreground",
              )}
            >
              {meta}
            </p>
          )}
        </div>
        {onForget && (
          <Button
            size="icon"
            variant="ghost"
            data-testid="home-room-delete"
            aria-label={t("entry.deleteRoomNamed", { name: title })}
            className="relative z-10 text-muted-foreground hover:text-destructive"
            onClick={() => setAsking(true)}
          >
            <Trash2 aria-hidden="true" className="size-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
